import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import './DailyTips.css';

function DailyTips({ userId }) {
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadDailyTip = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const data = await api.getDailyTip(userId);
      setTip(data);
    } catch (error) {
      console.error('Error loading daily tip:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadDailyTip();
    }
  }, [userId, loadDailyTip]);

  const getCategoryConfig = (category) => {
    const configs = {
      NUTRITION: { emoji: '🥗', color: 'var(--success-color)', bg: 'var(--success-light)' },
      EXERCISE: { emoji: '🏃', color: 'var(--info-color)', bg: 'var(--info-light)' },
      REST: { emoji: '😴', color: 'var(--primary-start)', bg: 'var(--bg-secondary)' },
      HEALTH: { emoji: '🏥', color: 'var(--warning-color)', bg: 'var(--warning-light)' },
      WELCOME: { emoji: '👋', color: 'var(--accent-color)', bg: 'var(--bg-secondary)' },
      default: { emoji: '💡', color: 'var(--primary-start)', bg: 'var(--bg-secondary)' }
    };
    return configs[category] || configs.default;
  };

  if (loading) {
    return (
      <div className="daily-tips-modern">
        <div className="daily-tips-header-modern">
          <div className="tips-header-content">
            <div className="tips-icon-placeholder"></div>
            <div>
              <h3 className="tips-title">💡 Daily Tip</h3>
              <p className="tips-subtitle">Loading your personalized tip...</p>
            </div>
          </div>
        </div>
        <div className="daily-tips-content-modern">
          <div className="loading-shimmer"></div>
        </div>
      </div>
    );
  }

  if (!tip) {
    return null;
  }

  const categoryConfig = getCategoryConfig(tip.category);

  return (
    <div className="daily-tips-modern">
      <div 
        className="daily-tips-header-modern"
        style={{ 
          background: `linear-gradient(135deg, ${categoryConfig.color}15 0%, ${categoryConfig.color}05 100%)`,
          position: 'relative',
          paddingLeft: `calc(12px + 16px)`,
          overflow: 'hidden'
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: `linear-gradient(180deg, ${categoryConfig.color} 0%, ${categoryConfig.color} 50%, ${categoryConfig.color} 100%)`,
          borderRadius: '8px 0 0 8px',
          opacity: 0.8
        }}></div>
        <div className="tips-header-content">
          <div 
            className="tips-category-icon"
            style={{ 
              background: categoryConfig.bg,
              color: categoryConfig.color
            }}
          >
            {categoryConfig.emoji}
          </div>
          <div className="tips-header-text">
            <h3 className="tips-title">Daily Tip</h3>
            <p className="tips-subtitle">{tip.category || 'General'} • Today</p>
          </div>
        </div>
        <button 
          className="refresh-btn-modern"
          onClick={() => loadDailyTip(true)}
          disabled={refreshing}
          title="Get new tip"
          aria-label="Refresh tip"
        >
          {refreshing ? (
            <span className="refresh-spinner"></span>
          ) : (
            <span className="refresh-icon">🔄</span>
          )}
        </button>
      </div>
      
      <div className="daily-tips-content-modern">
        <p className="tip-text-modern">{tip.tip}</p>
        
        {(tip.week || tip.trimester) && (
          <div className="tip-meta-modern">
            {tip.week && (
              <span className="meta-badge">
                <span className="meta-icon">📅</span>
                Week {tip.week}
              </span>
            )}
            {tip.trimester && (
              <span className="meta-badge">
                <span className="meta-icon">🤰</span>
                Trimester {tip.trimester}
              </span>
            )}
          </div>
        )}
        
        {tip.sourcedFrom && tip.sourceUrl && (
          <div className="tip-source-modern">
            <span className="source-label">Source:</span>
            <a 
              href={tip.sourceUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="source-link-modern"
            >
              {tip.sourcedFrom === 'ACOG' ? 'ACOG' : 
               tip.sourcedFrom === 'CDC' ? 'CDC' : 
               tip.sourcedFrom === 'MAYO_CLINIC' ? 'Mayo Clinic' : 
               tip.sourcedFrom === 'WHO' ? 'WHO' : tip.sourcedFrom}
              <span className="source-arrow">↗</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default DailyTips;
